/* global KENO_GLOBALS */
import $ from "vendor/zepto";
import ToastView from "keno/app/views/Toast";
import { random } from "lodash";
import EventsMixin from "keno/app/mixins/events";
import KenoCardTabView from "keno/app/views/KenoCardTab";
import ViewFactory from "keno/app/views/ViewFactory";
import KenoIntroView from "keno/app/views/KenoIntro";
import ChoicesView from "keno/app/views/Choices";
import HomeGlitchView from "keno/app/views/HomeGlitch";
import ButtonView from "keno/app/views/Button";
import renderTemplate from "keno/app/modules/templateLoader";
import animationUtil from "keno/app/modules/animationUtil";
import CoachingLightBox from "keno/app/views/CoachingLightBox";
import coachingMessages from "keno/app/config/coaching-messages";
import coachingMessagesMobile from "keno/app/mobile/config/coaching-messages";
import routes from "app/config/routes";

import SSOLightboxView from "keno/app/views/SSOLightbox";
import UniNavMessagesView from "keno/app/views/UniNavMessages";
import IdleMessageLightbox from "keno/app/mobile/views/IdleMessage";

const UNLOCK_ANIM_CLASS = `card--unlocking`;
const UNLOCK_ANIM_DONE_CLASS = `cardplay--unlocked`;
const SELECTED_CLASS = `card__number--selected`;
const USER_SELECTED_CLASS = `card__number--user-selected`;
const QUICKPICK_SELECTED_CLASS = `card__number--quickpick-selected`;

const SUBMIT_DISABLED_CLASS = `card__controls__btn--disabled`;

//Delay for idle message
const IDLE_MESSAGE_SECONDS = 30000;

//how long to idle message for -
const IDLE_MESSAGE_SHOW_FOR = 10000;

/**
 * This view class represents a playable Keno Card.
 * It is composed of other sub-views to handle other sections like the card tabs listing to the side
 * This view handles the events to select/deselect numbers and submit the card
 */
const KenoCardView = function() {};

Object.assign(KenoCardView.prototype, EventsMixin, {
	EVENTS: {
		SUBMIT: `submit`,
		GLITCH_SUBMIT: `glitchSubmit`,
		CHOICE_SELECT: `choiceSelect`,
		UPDATE_USER_NUMBERS: `updateUserNumbers`,
		SSO_LIGHTBOX_SUBMIT: `SSOLightboxSubmit`
	},

	init: function(appModel) {
		this.selectedNumbers = new Set();
		this.userSelectedNumbers = new Set();
		this.tabView = new KenoCardTabView();
		this.countdownView = ViewFactory.countdown();
		this.introView = new KenoIntroView();
		this.choicesView = new ChoicesView();

		this.glitchView = new HomeGlitchView();
		this.entryConfirmed = $(`.entry-confirmed`);

		this.tabView.addEvent(
			this.tabView.EVENTS.UNLOCK_ANIM_OUT,
			this.animateUnlock.bind(this)
		);
		this.tabView.addEvent(
			this.tabView.EVENTS.UNLOCK_ANIM_RESET,
			this.animateUnlockReset.bind(this)
		);
		this.tabView.addEvent(
			this.tabView.EVENTS.UNLOCK_ANIM_DONE,
			this.animateUnlockDone.bind(this)
		);
		this.tabView.addEvent(
			this.tabView.EVENTS.UPDATE_USER_NUMBERS,
			this.updateUserNumbers.bind(this)
		);
		this.introView.addEvent(
			this.introView.EVENTS.REVEALED,
			this.onIntroReveal.bind(this)
		);
		this.introView.addEvent(
			this.introView.EVENTS.OUT_ANIM_START,
			this.onIntroOut.bind(this)
		);
		this.choicesView.addEvent(
			this.choicesView.EVENTS.CHOICE_SELECT,
			this.onGameChoiceSelect.bind(this)
		);
		this.glitchView.addEvent(
			this.glitchView.EVENTS.SUBMIT,
			this.onGlitchSubmit.bind(this)
		);
		this._onCountdownTick = this.onCountdownTick.bind(this);

		this.globalDisplayQueue = appModel.globalDisplayQueue;
		this.appModel = appModel;

		this.coaching = new CoachingLightBox(window.KENO_GLOBALS.COACHING_SCREENS);
		this.idleMessage = new IdleMessageLightbox(true);
	},

	// called after each route handled, this
	// is the view's opportunity to hook into any new content in the
	// page as needed
	mount: function(ctx) {
		this.rootNode = $(`.cardplay`);
		this.cardNumbers = $("ol.card__numbers");
		this.gameTimestampId = parseInt(this.rootNode.attr(`data-gameid`), 10);
		this.cardNumber = parseInt(this.rootNode.attr(`data-cardnum`), 10);
		this.numToSelect = parseInt(this.rootNode.attr(`data-numbers`), 10);

		this.countdownView.mount(this.rootNode);
		if (KENO_GLOBALS.IS_PLAYING_NEXT) {
			this.countdownView.hide();
		}

		this.cardRootNode = $(`.card`);
		this.cardRootNode.on(
			`click`,
			`.card__number`,
			this.onNumberClicked.bind(this)
		);
		this.cardRootNode.on(
			`click`,
			`.card__controls__btn--clear`,
			this.onClear.bind(this)
		);
		this.cardRootNode.on(
			`click`,
			`.card__controls__btn--quick`,
			this.onQuickPick.bind(this)
		);
		this.submitBtn = this.cardRootNode.find(`.card__controls__btn--submit`);
		this.submitBtn.on(`click`, this.onSubmit.bind(this));

		const tabShadows = this.cardRootNode.hasClass(`card--withshadow`);

		this.tabView.mount(ctx, tabShadows);

		this.introView.mount(ctx);
		this.choicesView.mount(ctx);
		this.glitchView.mount(ctx);

		this.entryConfirmedWrapper = $(`.kenobar__paytable__drawer`);
		this.submitEntryContainer = $(`.submit-entry-confirmed-slider-container`);
		this.submitEntryWrapper = $(`.submit-entry-confirmed-slider`);

		this.entryBannerContest = $(`.entry-banner-contest`);

		// wait for display queue to clear before running animations
		if (!window.KENO_GLOBALS.IS_LOCKDOWN && !window.KENO_GLOBALS.IS_LOCKED) {
			if (this.globalDisplayQueue.isCurrentlyDisplaying()) {
				this.globalDisplayQueue.addEmptiedCallback(this.startAnims.bind(this));
			} else {
				window.setTimeout(() => {
					this.startAnims();
				}, 1500);
			}
		}

		// check the countdown timer for if we have enough time left to play a choice game
		if (
			!this.choicesView.hasEnoughTimeToPlay(
				this.countdownView.getRemainingSeconds()
			)
		) {
			this.onChoicesOutOfTime();
		} else {
			this.addTickListener();
		}

		this.idleTimeout = this.setIdleTimeout();

		this.numberRange = null;
		this.idleMessageShowing = false;

		this.isQuickPickRunning = false;

		// Coaching screens
		if (this.appModel.completedCardNumber > 1 && !this.isShownChoices()) {
			this.coaching.show(
				coachingMessages.default.msg,
				coachingMessages.default.top,
				coachingMessages.default.left
			);
		}

		if (window.KENO_GLOBALS.IS_LOCKED || window.KENO_GLOBALS.IS_LOCKDOWN) {
			window.setTimeout(() => {
				window.location = routes.HOME;
			}, 10000);
		}

		//paytable init
		this.initPaytableModal();
	},

	unmount: function(ctx) {
		if (this.cardRootNode) {
			this.cardRootNode.off(`click`);
		}
		if (this.submitBtn) {
			this.submitBtn.off(`click`);
		}

		this.cardRootNode = null;
		this.submitBtn = null;
		this.selectedNumbers.clear();
		this.numberRange = null;

		this.tabView.unmount(ctx);
		this.removeTickListener();
		this.countdownView.unmount();
		this.introView.unmount(ctx);
		this.choicesView.unmount(ctx);
		this.glitchView.unmount(ctx);
		clearTimeout(this.idleTimeout);
		this.isSubmitDisabled = false;
		this.idleMessageShowing = false;
		this.coaching.hideLightbox();
	},

	/**
	 * sets the timeout for the idle message, this is used repeatedly as the time shows every IDLE_MESSAGE_SECONDS
	 */
	setIdleTimeout: function() {
		if (window.KENO_GLOBALS.IS_LOCKED || window.KENO_GLOBALS.IS_LOCKDOWN)
			return;

		return window.setTimeout(() => {
			this.showIdleMessage();
		}, IDLE_MESSAGE_SECONDS);
	},
	/* clear idletimeout and hide idle message, reset idle timer */
	clearIdleTimeout: function() {
		this.idleMessage.hideLightbox();
		this.idleMessageShowing = false;
		clearTimeout(this.idleTimeout);
		this.idleTimeout = this.setIdleTimeout();
	},
	/** disable the idle message, for example during live drawing time */
	disableIdleTimeout: function() {
		this.idleMessage.hideLightbox();
		this.idleMessageShowing = false;
		clearTimeout(this.idleTimeout);
	},
	/** show the idle message, also resets the timeout after it has shown to that it can show repeatedly */
	showIdleMessage: function() {
		if (this.isQuickPickRunning === false) {
			this.idleMessage.show(
				coachingMessages.idleMessage.msg,
				coachingMessages.idleMessage.top,
				coachingMessages.idleMessage.left,
				coachingMessages.idleMessage.class,
				`.maincontent`
			);
		}
		this.idleMessageShowing = true;
		//reset idle message
		this.idleMessageShowTimeout = window.setTimeout(() => {
			this.clearIdleTimeout();
		}, IDLE_MESSAGE_SHOW_FOR);
	},
	/** shows create password lightbox upon submit for users without password */
	showCreatePasswordLightbox: function() {
		this.SSOLightboxView = new SSOLightboxView();
		this.SSOLightboxView.mount();
		this.SSOLightboxView.addEvent(
			this.SSOLightboxView.EVENTS.SSO_LIGHTBOX_SUBMIT,
			this.handleSSOLightBoxSubmit.bind(this)
		);
		//no thanks is equal to submit here because we still need to fire numbers
		this.SSOLightboxView.addEvent(
			this.SSOLightboxView.EVENTS.SSO_NO_THANKS,
			this.handleSSOLightBoxSubmit.bind(this)
		);
		this.SSOLightboxView.showSubmitNumbersLightbox();
	},
	/**
	 * handles submit of sso lightbox for no password users, submits numbers to the api upon successful password creation
	 */
	handleSSOLightBoxSubmit: function() {
		this.SSOLightboxView.hide();
		var selectedNumbersArray = new Array();
		this.selectedNumbers.forEach(
			function(num) {
				selectedNumbersArray.push(num);
			}.bind(this)
		);
		this.fireEvent(this.EVENTS.SSO_LIGHTBOX_SUBMIT, [
			this.gameTimestampId,
			this.cardNumber,
			selectedNumbersArray
		]);
		//now fire the submit event to the controller since user has successfully created a password
	},
	updateUserNumbers: function() {
		this.fireEvent(this.EVENTS.UPDATE_USER_NUMBERS);
	},
	/** update user played games on the keno card
	 * @param {array} games - user played games
	 */
	updateUserNumbersHTML: function(games) {
		this.tabView.updateUserNumbersHTML(games);
	},
	/**
	 * starts the initial load animation upon loading of a keno card
	 */
	startAnims: function() {
		if (!this.introView.hasIntro() && !this.glitchView.hasError()) {
			this.tabView.runAnimation();
		} else {
			this.introView.runAnimation(2000);
		}
	},
	/** handles the keno card submission, gets user picked numbers from the card and submits to the api, handles error scenario */
	onSubmit: function(ev) {
		if (this.isSubmitDisabled) {
			return;
		}

		if (this.isQuickPickRunning) {
			return;
		}

		ev.preventDefault();
		if (this.hasSelectedAllNumbers()) {
			var selectedNumbersArray = new Array();
			this.selectedNumbers.forEach(
				function(num) {
					selectedNumbersArray.push(num);
				}.bind(this)
			);

			this.fireEvent(this.EVENTS.SUBMIT, [
				this.gameTimestampId,
				this.cardNumber,
				selectedNumbersArray
			]);
            if(PCHEvents) PCHEvents.cmd.push(() => { PCHEvents.triggerEvent('keno_play'); });

			//this.showSweepstakesEntryConfirmed();
			//this.showSubmitEntryConfirmed();
		} else {
			this.$oops = this.$oops || $(renderTemplate(`shared/kenocard-oops.html`));
			document.body.appendChild(this.$oops.get(0));
			this.$oops.modal({
				backdrop: false
			});
		}
	},

	onDuplicateNumbers: function(message) {
		if (this.$oopsDuplicate) {
			document.body.removeChild(this.$oopsDuplicate.get(0));
		}
		this.$oopsDuplicate = $(
			renderTemplate(`shared/kenocard-oops-duplicate.html`, {
				message: message
			})
		);
		document.body.appendChild(this.$oopsDuplicate.get(0));

		this.$oopsDuplicate.modal({
			backdrop: false
		});
	},
	/**
	 * clears selected numbers on the card when clear button is pressed
	 */
	onClear: function(ev) {
		if (ev) {
			ev.preventDefault();
		}

		if (this.isQuickPickRunning) {
			return;
		}

		this.cardRootNode
			.find(`.` + SELECTED_CLASS)
			.removeClass(SELECTED_CLASS)
			.removeClass(USER_SELECTED_CLASS)
			.removeClass(QUICKPICK_SELECTED_CLASS);
		this.selectedNumbers.clear();
		this.userSelectedNumbers.clear();
		this.disableSubmitBtn();
		this.coaching.show(
			coachingMessages.default.msg,
			coachingMessages.default.top,
			coachingMessages.default.left
		);
	},
	/**
	 *
	 * @param {int} num - number to clear
	 * @param {*} $targ - $object of number to clear
	 */
	clearNumber: function(num, $targ) {
		this.selectedNumbers.delete(num);
		$targ
			.removeClass(SELECTED_CLASS)
			.removeClass(USER_SELECTED_CLASS)
			.removeClass(QUICKPICK_SELECTED_CLASS);
		this.disableSubmitBtn();
		this.userSelectedNumbers.delete(num);
		if (this.removePickedNumberFromControlBar) {
			this.removePickedNumberFromControlBar(num);
		}
	},
	/**
	 * clears all quick picked numbers but leaves user selected numbers
	 */
	onClearQuickpicks: function() {
		//clear only quickpick selected numbers
		this.selectedNumbers.forEach(num => {
			if (!this.userSelectedNumbers.has(num)) {
				let $targ = this.cardNumbers.find(`[data-number='${num}']`);
				this.clearNumber(num, $targ);
				if (this.removePickedNumberFromControlBar) {
					this.removePickedNumberFromControlBar(num);
				}
			}
		});
	},
	/**
	 * disables the submit button so user cannot send multiple numbers to the api or when user doesn't have enough numbers picked
	 */
	disableSubmit: function() {
		this.isSubmitDisabled = true;
		ButtonView.disableForSubmitPending(this.submitBtn);
		if (this.choicesView && this.choicesView.isShown()) {
			this.choicesView.disableSubmit();
		}
	},
	/**
	 * enables the submit button when user has selected enough numbers
	 */
	enableSubmit: function() {
		this.isSubmitDisabled = false;
		ButtonView.enableFromSubmitPending(this.submitBtn);
		if (this.choicesView.isShown()) {
			this.choicesView.enableSubmit();
		}
	},

	disableSubmitBtn: function() {
		this.submitBtn.addClass(SUBMIT_DISABLED_CLASS);
	},

	enableSubmitBtn: function() {
		this.submitBtn.removeClass(SUBMIT_DISABLED_CLASS);
	},
	/** get the range of available numbers, eg. 1-99 */
	getNumberRange: function() {
		if (this.numberRange === null) {
			this.numberRange = [
				parseInt(
					this.cardRootNode
						.find(`.card__number:first-child`)
						.attr(`data-number`),
					10
				),
				parseInt(
					this.cardRootNode
						.find(`.card__number:last-child`)
						.attr(`data-number`),
					10
				)
			];
		}
		return this.numberRange;
	},

	getCardNumber: function() {
		return this.cardNumber;
	},

	getGameTimestampId: function() {
		return this.gameTimestampId;
	},
	/**
	 * handles the quickpick functionality, generates random numbers for the users remaining picks
	 */
	onQuickPick: function(ev) {
		if (this.isQuickPickRunning) {
			return;
		}

		// when quick picking and all numbers already selected,
    	// just clear the user selections and quick pick all new numbers
    	// otherwise, we only quick pick the remaining numbers and keep
    	// the ones the user personally selected
    	if (this.userSelectedNumbers.size == this.numToSelect) {
			this.userSelectedNumbers.clear();
		}

		this.coaching.hideLightbox();
		this.idleMessage.hideLightbox();
		this.onClearQuickpicks();
		const range = this.getNumberRange();
		const randomNum = random.bind(null, range[0], range[1]);
		let num;

		//pick rest of quickpick numbers
		for (let i = this.userSelectedNumbers.size; i < this.numToSelect; i++) {
			do {
				num = randomNum();
			} while (this.selectedNumbers.has(num));
			this.selectedNumbers.add(num);
		}
		this.selectNumWithFun();
	},

	selectNumWithFun: function() {
		let range = this.getNumberRange();
		var randomNum = random.bind(null, range[0], range[1]);
		let numPadEl = undefined;
		let self = this;
		const allSelectedArray = this.convertToArray(this.selectedNumbers);
		const userSelectedArray = this.convertToArray(this.userSelectedNumbers);

		let selectedLength = allSelectedArray.length;
		let selectedIterator = userSelectedArray.length;
		let counter = 0;
		let randomSelectCount = 3; //can make this number larger to show more run through of numbers
		let delayBetweenSelections = 250;
		let delayBetweenNextNumber = 200;

		if (window.isMobile) {
			randomSelectCount = 2; //can make this number larger to show more run through of numbers
			delayBetweenSelections = 150;
			delayBetweenNextNumber = 100;
		}

		if(window.PCH.env === 'qa' || window.PCH.env === 'dev') {
			randomSelectCount = 1;  //set low to not slow testing
			delayBetweenSelections = 50;
			delayBetweenNextNumber = 100;
		}

		this.isQuickPickRunning = true;

		setNumber();

		function setNumber() {
			if (numPadEl != undefined) {
				numPadEl.removeClass(SELECTED_CLASS);
			}
			let num = randomNum();

			//skip slected and user selcted number here
			while (allSelectedArray.indexOf(num) != -1) {
				num = randomNum();
			}

			numPadEl = self.cardRootNode.find(
				'.card__number[data-number="' + num + '"]'
			);
			numPadEl.addClass(SELECTED_CLASS);
			counter++;

			if (counter <= randomSelectCount) {
				setTimeout(setNumber, delayBetweenSelections);
			} else {
				let setNum = allSelectedArray[selectedIterator++];

				numPadEl.removeClass(SELECTED_CLASS);
				numPadEl = self.cardRootNode.find(
					'.card__number[data-number="' + setNum + '"]'
				);
				numPadEl.addClass(SELECTED_CLASS + " " + QUICKPICK_SELECTED_CLASS);
				numPadEl = undefined; //skip from clearing this on next pass

				if (self.addPickedNumberToControlBar) {
					self.addPickedNumberToControlBar(setNum);
				}

				//if not at end of the numbers list...
				if (selectedIterator != selectedLength) {
					//reset and go again
					counter = 0;
					setTimeout(setNumber, delayBetweenNextNumber);
				} else {
					self.enableSubmit();
					self.enableSubmitBtn();
					self.eventTracker(`QuickPick`);

					if (window.isMobile) {
						self.coaching.show(
							coachingMessagesMobile.submit.msg,
							coachingMessagesMobile.submit.top,
							coachingMessagesMobile.submit.left,
							coachingMessagesMobile.submit.class,
							`body`
						);
					} else {
						self.coaching.show(
							coachingMessages.submit.msg,
							coachingMessages.submit.top,
							coachingMessages.submit.left
						);
					}
					self.isQuickPickRunning = false;
				}
			}
		}
	},

	convertToArray: function(setObject) {
		if (Array.from) {
			return Array.from(setObject);
		} else {
			//if not supported we can do it ourselves
			let newArray = new Array();

			setObject.forEach(item => {
				newArray.push(item);
			});
			if (setObject.size === newArray.length) {
				console.log("Converted to same size array.");
			} else {
				console.log(
					"Problem Array not right size.. Set: " +
						setObject.size +
						" Array: " +
						newArray.length
				);
			}
			return newArray;
		}
	},
	/**
	 * shows the user selected numbers
	 */
	onNumberClicked: function(ev) {
		ev.preventDefault();

		if (this.isQuickPickRunning) {
			return;
		}

		this.handleNumberClicked(ev);

		if (this.selectedNumbers.size >= this.numToSelect) {
			this.coaching.show(
				coachingMessages.submit.msg,
				coachingMessages.submit.top,
				coachingMessages.submit.left
			);
		} else {
			this.coaching.show(
				coachingMessages.default.msg,
				coachingMessages.default.top,
				coachingMessages.default.left
			);
		}
	},
	/**
	 * handles a number click, determines if number is being selected or deselected and adds or remove from selectedNumbers object, shows or disables submit button accordingly
	 */
	handleNumberClicked: function(ev) {
		let $targ = $(ev.target);

		$targ = $targ.hasClass(`.card__number`)
			? $targ
			: $targ.parents(`.card__number`);
		if ($targ.length === 0) {
			return;
		}
		const num = parseInt($targ.attr(`data-number`), 10);

		// deselect the number
		if (this.selectedNumbers.has(num)) {
			this.clearNumber(num, $targ);
		} else if (!this.hasSelectedAllNumbers()) {
			// select tje number
			this.selectedNumbers.add(num);
			this.userSelectedNumbers.add(num);
			$targ.addClass(SELECTED_CLASS + " " + USER_SELECTED_CLASS);
			if (this.hasSelectedAllNumbers()) {
				this.enableSubmitBtn();
			}
		}
	},
	/**
	 * returns true if user has selected all of their numbers for this card, false otherwise.
	 */
	hasSelectedAllNumbers: function() {
		return this.selectedNumbers.size >= this.numToSelect;
	},

	animateUnlock: function() {
		this.cardRootNode.addClass(UNLOCK_ANIM_CLASS);
	},

	animateUnlockDone: function() {
		this.rootNode.addClass(UNLOCK_ANIM_DONE_CLASS);
	},

	animateUnlockReset: function() {
		this.cardRootNode.removeClass(UNLOCK_ANIM_CLASS);
		this.rootNode.removeClass(UNLOCK_ANIM_DONE_CLASS);
	},

	onIntroOut: function() {
		this.tabView.runAnimation();
	},

	onIntroReveal: function() {
		this.rootNode.removeClass(`cardplay--intro`);
	},

	addTickListener: function() {
		this.countdownView.addEvent(
			this.countdownView.EVENTS.TICK,
			this._onCountdownTick
		);
	},

	removeTickListener: function() {
		this.countdownView.removeEvent(
			this.countdownView.EVENTS.TICK,
			this._onCountdownTick
		);
	},

	onCountdownTick: function(remainingSeconds) {
		if (this.countdownView.getRemainingSeconds() === 0) {
			window.setTimeout(() => {
				window.location.reload();
			}, 3000);
		}
	},
	/**
	 * displays choices out of time overlay to prevent user from starting a IW path when they don't have enough time
	 */
	onChoicesOutOfTime: function() {
		if (
			this.choicesView &&
			this.choicesView.isShown() &&
			!KENO_GLOBALS.IS_PLAYING_NEXT
		) {
			this.choicesView.showOutOfTimeOverlay();
			this.disableIdleTimeout();
		}
	},

	onGlitchSubmit: function() {
		if (!this.isSubmitDisabled) {
			this.fireEvent(this.EVENTS.GLITCH_SUBMIT);
		}
	},

	onGameChoiceSelect: function(choiceId) {
		if (!this.isSubmitDisabled) {
			this.fireEvent(this.EVENTS.CHOICE_SELECT, [choiceId]);
		}
	},
	/**
	 * shows a sweepstakes entry confirmed message when the api returns one
	 */
	showSweepstakesEntryConfirmed: function(data) {
		const contestEntryValue = data.sweepstake.contestMessage;

		UniNavMessagesView.showSweepstakesContestEntry(contestEntryValue);
	},
	/**
	 * shows a promotion entry confirmed message when the api returns one
	 */
	showPromotionEntryConfirmed: function(imgPath, textContent) {
		let onClose = null,
			view = null;

		const promise = new Promise(resolve => {
			view = new ToastView(`shared/promotion-entry-confirmed.html`, {
				imgPath: imgPath,
				textContent: textContent
			});
			onClose = () => {
				view.removeEvent(view.EVENTS.CLOSED, onClose);
				resolve();
			};

			view.addEvent(view.EVENTS.CLOSED, onClose);
			view.show();
		});

		return promise;
	},
	hidePromotionEntryBanner: function() {
		animationUtil.animate(
			this.entryBannerContest,
			animationUtil.ANIMS.FADE_OUT,
			() => {
				this.entryBannerContest.hide();
			}
		);
		$(`body`).removeClass(`top-banner-showing`);
	},

	hideEntryConfirmed: function() {
		animationUtil.animate(
			this.entryConfirmedWrapper,
			animationUtil.ANIMS.SLIDE_OUT_DOWN,
			function() {
				this.entryConfirmed.appendTo(document.body);
				this.entryConfirmed.hide();
				this.submitEntryContainer.hide();
			}.bind(this)
		);
	},

	showSubmitEntryConfirmed: function() {
		this.submitEntryContainer.show();
		animationUtil.animateFast(
			this.submitEntryWrapper,
			animationUtil.ANIMS.SLIDE_IN_UP,
			function() {
				window.setTimeout(
					function() {
						this.hideSubmitEntryConfirmed();
					}.bind(this),
					2000
				);
			}.bind(this),
			true
		);
	},

	hideSubmitEntryConfirmed: function() {
		animationUtil.animateFast(
			this.submitEntryWrapper,
			animationUtil.ANIMS.SLIDE_OUT_DOWN,
			function() {}.bind(this)
		);
	},

	eventTracker: function(eventAction) {
		// Track event
		if (typeof window.PCHGA !== `undefined`) {
			const url = `${window.location.href}card${
				this.appModel.completedCardNumber
			}`; // start the card at 1

			window.PCHGA.trackEvent(`Keno/${device}`, eventAction, url);
		}
	},
	/**
	 * determines if the user is seeing the choices screen
	 */
	isShownChoices: function() {
		const rootNode = $(`.choices__games`);

		return !!rootNode && rootNode.length > 0;
	},

	/**
	 * set open (link) and close ('X' and click behind) events for paytable modal
	 */
	initPaytableModal: function() {
		this.paytableLink = $(".cardplay__paytable");
		this.paytableLink.on("click", function() {
			$(".paytable-modal").removeClass("paytable-modal__hide");
		});

		this.paytableClose = $(".paytable__closebutton");
		this.paytableClose.on("click", function() {
			$(".paytable-modal").addClass("paytable-modal__hide");
		});

		this.paytableOverlay = $(".paytable-modal .c-overlay");
		this.paytableOverlay.on("click", function() {
			$(".paytable-modal").addClass("paytable-modal__hide");
		});
	}
});

export default KenoCardView;
